import React, { useEffect, useState } from 'react';
import { useDashboardContext } from '../../../components/providers/DashboardProvider';
import { useAuth } from '../../../components/providers/AuthProvider';
import { Box } from '@mui/material';
import CurrentCard from './CurrentCard';
import DayCard from './DayCard';
import MonthCard from './MonthCard';
import YearCard from './YearCard';
import UnavailableCard from './UnavailableCard';
import RawDataPanel from '../RawDataPanel';
import { splitGridEnergy } from './energy';

const GridPage = () => {

  const { user } = useAuth();
  const isAdmin = user?.profile?.user_type === 'admin';

  const { pollingCounter } = useDashboardContext();

  const {
    fetchClusterGridData, gridData,
  } = useDashboardContext();

  const [dataValid, setDataValid] = useState(false);

  const [phaseCurrent1, setPhaseCurrent1] = useState(0);
  const [phaseCurrent2, setPhaseCurrent2] = useState(0);
  const [phaseCurrent3, setPhaseCurrent3] = useState(0);
  const [powerAvg, setPowerAvg] = useState(0);
  const [powerMaxToday, setPowerMaxToday] = useState(0);
  const [energyImportToday, setEnergyImportToday] = useState(0);
  const [energyExportToday, setEnergyExportToday] = useState(0);

  const [tsHourlyEnergy, setTsHourlyEnergy] = useState([]);
  const [tsHourlyPower, setTsHourlyPower] = useState([]);

  const [tsDailyEnergy, setTsDailyEnergy] = useState([]);
  const [tsDailyPower, setTsDailyPower] = useState([]);

  const [tsMonthlyEnergy, setTsMonthlyEnergy] = useState([]);
  const [tsMonthlyPower, setTsMonthlyPower] = useState([]);

  const runRoutine = async () => {
    console.log("Polling in Grid");
    fetchClusterGridData();
  }

  const processGridData = (gridData) => {
    // console.log(gridData);

    setPhaseCurrent1(0);
    setPhaseCurrent2(0);
    setPhaseCurrent3(0);
    setPowerAvg(0);
    setPowerMaxToday(0);
    setEnergyImportToday(0);
    setEnergyExportToday(0);

    setTsHourlyEnergy([]);
    setTsHourlyPower([]);

    setTsDailyEnergy([]);
    setTsDailyPower([]);

    setTsMonthlyEnergy([]);
    setTsMonthlyPower([]);

    if (gridData && gridData.blocks_data && Object.keys(gridData.blocks_data).length > 0) {

      let meterIndex = 0;

      let _tsHourlyEnergy = []
      let _tsHourlyPower = []
      let _tsDailyEnergy = []
      let _tsDailyPower = []
      let _tsMonthlyEnergy = []
      let _tsMonthlyPower = []

      // Iterate through all blocks
      Object.keys(gridData.blocks_data).forEach(blockId => {
        const blockData = gridData.blocks_data[blockId];

        // Iterate through all meters, in case somehow there are more than one meter
        if (blockData.meters_data) {
          Object.keys(blockData.meters_data).forEach(meterId => {
            const meterData = blockData.meters_data[meterId];

            // Latest data
            if (meterData.meter_data_latest) {
              const latestData = meterData.meter_data_latest;
              if (latestData.I_1) {
                setPhaseCurrent1(value => value + latestData.I_1);
              }
              if (latestData.I_2) {
                setPhaseCurrent2(value => value + latestData.I_2);
              }
              if (latestData.I_3) {
                setPhaseCurrent3(value => value + latestData.I_3);
              }
              if (latestData.P_active_total) {
                setPowerAvg(value => value + latestData.P_active_total);
              }
              if (latestData.P_active_max_td) {
                setPowerMaxToday(value => value + latestData.P_active_max_td);
              }
              const { importE, exportE } = splitGridEnergy(latestData, "E_active_import_td", "E_active_export_td", "E_active_td");
              if (importE) {
                setEnergyImportToday(value => value + importE);
              }
              if (exportE) {
                setEnergyExportToday(value => value + exportE);
              }
            }

            // Time-series hourly
            // Go through the hourly data, put into the 24h plot
            if (meterData.meter_data_ts_hour) {
              const dataTsHourly = meterData.meter_data_ts_hour;
              // console.log(dataTsHourly);
              _tsHourlyEnergy.push(
                {
                  x: [],
                  yImport: [],
                  yExport: [],
                });

              _tsHourlyPower.push(
                {
                  x: [],
                  y: [],
                  name: "Peak power"
                });

              // Iterate through all epochs
              Object.keys(dataTsHourly).forEach(epoch => {
                const epochData = dataTsHourly[epoch];
                // console.log(epochData);
                // Keep every timestamped epoch (null = gap) so all bar traces share the same x positions
                if (epochData.local_datetime_str) {
                  _tsHourlyEnergy[meterIndex]["x"].push(epochData.local_datetime_str);
                  const { importE, exportE } = splitGridEnergy(epochData);
                  _tsHourlyEnergy[meterIndex]["yImport"].push(importE);
                  _tsHourlyEnergy[meterIndex]["yExport"].push(exportE);
                }
                if (epochData.P_active_max && epochData.P_active_max !== null) {
                  _tsHourlyPower[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsHourlyPower[meterIndex]["y"].push(epochData.P_active_max);
                }
              }); // Iterate through all epochs
            }

            // Time-series daily
            // Go through the daily data, put into the 30 days plot
            if (meterData.meter_data_ts_day) {
              const dataTsDaily = meterData.meter_data_ts_day;
              // console.log(dataTsMonthly);
              _tsDailyEnergy.push(
                {
                  x: [],
                  yImport: [],
                  yExport: [],
                });

              _tsDailyPower.push(
                {
                  x: [],
                  y: [],
                  name: "Peak power"
                });

              // Iterate through all epochs
              Object.keys(dataTsDaily).forEach(epoch => {
                const epochData = dataTsDaily[epoch];
                // console.log(epochData);
                // Keep every timestamped epoch (null = gap) so all bar traces share the same x positions
                if (epochData.local_datetime_str) {
                  _tsDailyEnergy[meterIndex]["x"].push(epochData.local_datetime_str);
                  const { importE, exportE } = splitGridEnergy(epochData);
                  _tsDailyEnergy[meterIndex]["yImport"].push(importE);
                  _tsDailyEnergy[meterIndex]["yExport"].push(exportE);
                }
                if (epochData.P_active_max && epochData.P_active_max !== null) {
                  _tsDailyPower[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsDailyPower[meterIndex]["y"].push(epochData.P_active_max);
                }
              }); // Iterate through all epochs
            }

            // Time-series monthly
            if (meterData.meter_data_ts_month) {
              const dataTsMonthly = meterData.meter_data_ts_month;
              // console.log(dataTsMonthly);
              _tsMonthlyEnergy.push(
                {
                  x: [],
                  yImport: [],
                  yExport: [],
                });

              _tsMonthlyPower.push(
                {
                  x: [],
                  y: [],
                  name: "Peak power"
                });

              // Iterate through all epochs
              Object.keys(dataTsMonthly).forEach(epoch => {
                const epochData = dataTsMonthly[epoch];
                // console.log(epochData);
                // Keep every timestamped epoch (null = gap) so all bar traces share the same x positions
                if (epochData.local_datetime_str) {
                  _tsMonthlyEnergy[meterIndex]["x"].push(epochData.local_datetime_str);
                  const { importE, exportE } = splitGridEnergy(epochData);
                  _tsMonthlyEnergy[meterIndex]["yImport"].push(importE);
                  _tsMonthlyEnergy[meterIndex]["yExport"].push(exportE);
                }
                if (epochData.P_active_max && epochData.P_active_max !== null) {
                  _tsMonthlyPower[meterIndex]["x"].push(epochData.local_datetime_str);
                  _tsMonthlyPower[meterIndex]["y"].push(epochData.P_active_max);
                }
              }); // Iterate through all epochs
            }

            meterIndex = meterIndex + 1;

          }) // Iterate through all meters
        }

      }) // Iterate through all blocks 

      // console.log(_tsHourlyEnergy);
      setTsHourlyEnergy(_tsHourlyEnergy);
      setTsHourlyPower(_tsHourlyPower);
      setTsDailyEnergy(_tsDailyEnergy);
      setTsDailyPower(_tsDailyPower);
      setTsMonthlyEnergy(_tsMonthlyEnergy);
      setTsMonthlyPower(_tsMonthlyPower);

      setDataValid(true);
    }
  }

  useEffect(() => {
    if (pollingCounter != 0) {
      runRoutine();
    }
  }, [pollingCounter]);

  useEffect(() => {
    processGridData(gridData);
  }, [gridData])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        pt: 2,
        gap: 2,
      }}
    >
      {(gridData != null && dataValid) ?
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}>
          <CurrentCard
            current1={phaseCurrent1}
            current2={phaseCurrent2}
            current3={phaseCurrent3}
            powerAvg={powerAvg}
            powerMaxToday={powerMaxToday}
            energyImportToday={energyImportToday}
            energyExportToday={energyExportToday}
          />
          <DayCard
            seriesEnergy={tsHourlyEnergy}
            seriesPeakPower={tsHourlyPower}
          />
          <MonthCard
            seriesEnergy={tsDailyEnergy}
            seriesPeakPower={tsDailyPower}
          />
          <YearCard
            seriesEnergy={tsMonthlyEnergy}
            seriesPeakPower={tsMonthlyPower}
          />
        </Box>
        :
        <UnavailableCard />
      }

      {gridData && isAdmin && <RawDataPanel data={gridData} />}

    </Box>
  )
}

export default GridPage;